import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

import { Autocomplete, Box, Dialog, FormControl, IconButton, TextField, Typography } from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';

import { RootState } from 'src/types';
import { fetchCompanyDocTypes } from 'src/doc-types/actions';
import SubmitBtn from 'src/shared/buttons/SubmitBtn';
import CancelBtn from 'src/shared/buttons/CancelBtn';
import styles from './style';

interface PropTypes {
  open: boolean;
  onClose: () => void;
  nodeType: string | null;
  nodeId: string | null;
  label: string | null;
  configuration: Record<string, any>;
  isSaving?: boolean;
  // eslint-disable-next-line no-unused-vars
  onConfigurationSave: (nodeId: string, configuration: Record<string, any>) => void;
}

const DocumentTypeComponent = (props: PropTypes) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { open, onClose, nodeId, configuration, isSaving = false, onConfigurationSave } = props;
  const docTypes: Record<string, any>[] = useSelector((state: RootState) => state.docTypes.companyDocTypes);

  const [selectedDocType, setSelectedDocType] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (docTypes.length === 0) {
      dispatch(fetchCompanyDocTypes() as any);
    }
  }, [dispatch, docTypes.length]);

  useEffect(() => {
    if (configuration) {
      setSelectedDocType(configuration.selectedDocumentType || undefined);
    }
  }, [configuration]);

  const handleChangeAutoComplete = (value: Record<string, any>) => {
    if (value && (value.documentType)) {
      setSelectedDocType(value.documentType);
    }
  };

  const handleSave = () => {
    if (nodeId) {
      const documentTypeConfig = {
        selectedDocumentType: selectedDocType,
      };
      onConfigurationSave(nodeId, documentTypeConfig);
    }
  };

  return (
    <Dialog
      open={open}
      sx={styles.modal}
      onClose={onClose}
    >
      <Box sx={styles.container}>
        <Box sx={styles.modalHeader}>
          <Box sx={styles.titleContainer}>
            <Typography sx={styles.headerTitle}>
              {t('WORKFLOW_COMPONENT_DOC_TYPE')}
            </Typography>
            <IconButton sx={styles.closeBtn} onClick={onClose}>
              <CloseIcon sx={styles.closeIcon} />
            </IconButton>
          </Box>
        </Box>
        <Box sx={styles.modalContent}>
          <Box sx={styles.addNewForm}>
            <Box sx={styles.fieldContainer}>
              <Typography sx={styles.descr}>{t('WORKFLOW_COMPONENT_DOC_TYPE_DESCRIPTION')}</Typography>
            </Box>
            <Box sx={styles.fieldContainer}>
              <Typography sx={styles.label}>{t('DOC_TYPES_TABLE_TYPE')}</Typography>
              <FormControl fullWidth sx={styles.formControl}>
                <Autocomplete
                  fullWidth
                  onChange={(_, val) => handleChangeAutoComplete(val as unknown as Record<string, any>)}
                  value={docTypes.find((d) => d.documentType === selectedDocType) || null}
                  options={docTypes}
                  getOptionLabel={(option) => typeof option === 'string' ? option : t(option.name)}
                  getOptionKey={(option) => option.documentType}
                  filterOptions={(options, state) => options.filter((option) => {
                    if (option.documentType && option.name) {
                      return option.documentType.toLowerCase().includes(state.inputValue.toLowerCase())
                        || option.name.toLowerCase().includes(state.inputValue.toLowerCase());
                    }
                    return false;
                  })}
                  sx={styles.autoComplete}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="name"
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                      }}
                      sx={styles.autoCompleteTextField}
                    />
                  )}
                />
              </FormControl>
            </Box>
          </Box>
        </Box>
        <Box sx={styles.modalFooter}>
          <Box display="flex" justifyContent="flex-end" gap="16px">
            <CancelBtn
              title={t('DOC_TYPES_TABLE_ADD_NEW_CANCEL')}
              onClick={onClose}
            />
            <SubmitBtn
              title={t('DOC_TYPES_FIELDS_MANAGE_SAVE')}
              disabled={isSaving || selectedDocType === undefined}
              onClick={handleSave}
            />
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}

export default DocumentTypeComponent;
